import React, { useState, useEffect } from 'react';
import { Form, Row, Col, Button, Modal } from 'react-bootstrap';
import LeaveLayout from '../../../components/LeaveLayout.jsx';
import LeaveReportLayout from '../../../components/LeaveReportLayout.jsx';
import { SiGoogledocs } from "react-icons/si";
import { TbLoader3 } from "react-icons/tb"; // Importing the loader icon
import * as XLSX from 'xlsx'; // For Excel export
import jsPDF from 'jspdf'; // For PDF export
import 'jspdf-autotable'; // For PDF table
import './emps-leave-reports.css';
import { IoEye } from "react-icons/io5";

export default function EmpsLeaveReports() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null); // For storing selected leave details

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch('https://backend.ems.uk.hiradentallab.com/api/user/getAllUsers');
        if (!response.ok) {
          throw new Error('Failed to fetch users');
        }
        const data = await response.json();
        setUsers(data);
      } catch (error) {
        console.error('Error fetching users:', error);
        alert('Failed to fetch user data. Please try again.');
      }
    };

    fetchUsers();
  }, []);

  const validateForm = () => {
    const errors = {};
    if (!selectedUser) errors.selectedUser = 'UserName is required';
    if (!startDate) errors.startDate = 'Start date is required';
    if (!endDate) errors.endDate = 'End date is required';
    return errors;
  };

  const handleGenerateReport = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setShowErrors(true);
    } else {
      setIsGenerating(true);
      setShowErrors(false);
      setShowTable(false);

      try {
        // Fetching leave report data by username and date range
        const response = await fetch(`https://backend.ems.uk.hiradentallab.com/api/leaves/leaves/by-username?username=${selectedUser}&startDate=${startDate}&endDate=${endDate}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch report data');
        }

        const data = await response.json();
        setReportData(data.leaves); // Assuming `data.leaves` is the correct response structure
        setShowTable(true);
      } catch (error) {
        console.error('Error fetching report data:', error);
        alert('Error fetching report data. Please try again.');
      } finally {
        setIsGenerating(false);
      }
    }
  };

  const handleExportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(reportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'LeaveReport');
    XLSX.writeFile(wb, 'LeaveReport.xlsx');
  };

  const handleExportToPDF = () => {
    const doc = new jsPDF();
    doc.text('Leave Report', 10, 10);
    doc.autoTable({
      head: [['SR', 'Name', 'Leave Type', 'Start Date', 'End Date', 'Applied Duration', 'Applied Date', 'HR Approval', 'Admin Approval']],
      body: reportData.map((data, index) => [
        index + 1,
        data.name,
        data.leaveType,
        new Date(data.startDate).toLocaleDateString(), // Formatting the start date
        new Date(data.endDate).toLocaleDateString(),   // Formatting the end date
        data.appliedDuration,
        new Date(data.appliedDate).toLocaleDateString(), // Formatting the applied date
        data.hrApproval,
        data.adminApproval,
      ]),
    });
    doc.save('LeaveReport.pdf');
  };

  // Function to handle the eye button click to show modal
  const handleShowModal = (leaveData) => {
    setSelectedLeave(leaveData); // Set selected leave data
    setShowModal(true); // Show the modal
  };

  // Function to handle closing the modal
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedLeave(null); // Reset selected leave data
  };

  return (
    <LeaveLayout>
      <LeaveReportLayout>
        <h1 className="emps-leave-report-title"><SiGoogledocs /> All Report</h1>
        <hr />
        <Form className="emps-leave-report-form">
          <Row>
            <Form.Group as={Col} xs={12} md={4} controlId="formGridUserName">
              <Form.Label>UserName <span className='required'>*</span>:</Form.Label>
              <Form.Select
                name="username"
                required
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="form-control fancy-select"
                isInvalid={showErrors && !selectedUser}
              >
                <option value="">Select a Username</option>
                {users.map((user) => (
                  <option key={user._id} value={user.username}>{user.username}</option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {showErrors && !selectedUser ? 'UserName is required' : ''}
              </Form.Control.Feedback>
            </Form.Group>
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label htmlFor="start_date">
                  Start date <span className="required">*</span> :
                </Form.Label>
                <Form.Control
                  type="date"
                  id="start_date"
                  name="start_date"
                  required
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  isInvalid={showErrors && !startDate}
                />
                <Form.Control.Feedback type="invalid">
                  {showErrors && !startDate ? 'Start date is required' : ''}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col xs={12} md={4}>
              <Form.Group>
                <Form.Label htmlFor="end_date">
                  End date <span className="required">*</span> :
                </Form.Label>
                <Form.Control
                  type="date"
                  id="end_date"
                  name="end_date"
                  required
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  isInvalid={showErrors && !endDate}
                />
                <Form.Control.Feedback type="invalid">
                  {showErrors && !endDate ? 'End date is required' : ''}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Button
            className="generate-report-button"
            type="button"
            onClick={handleGenerateReport}
            disabled={isGenerating}
          >
            {isGenerating ? (
              <TbLoader3 className="icon-rotate" />
            ) : (
              'Generate Report'
            )}
          </Button>
        </Form>

        {showTable && !isGenerating && (
          <div className="emps-leave-report-table-container">
            <div className="table-actions">
              <Row className="w-100">
                <Col xs={12} md={6} className="d-flex align-items-center mb-3 justify-content-start">
                  <Button
                    variant="secondary"
                    className="export-button"
                    onClick={handleExportToExcel}
                  >
                    Excel
                  </Button>
                  <Button
                    variant="secondary"
                    className="export-button"
                    onClick={handleExportToPDF}
                  >
                    PDF
                  </Button>
                </Col>
              </Row>
            </div>
            <div className="emps-leave-report-table">
              <table className="table table-striped">
                <thead className='leave-report-table-header'>
                  <tr>
                    <th>SR</th>
                    <th>Name</th>
                    <th>Leave Type</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Applied Duration</th>
                    <th>Applied Date</th>
                    <th>HR Approval</th>
                    <th>Admin Approval</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.map((data, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{data.name}</td>
                      <td>{data.leaveType}</td>
                      <td>{new Date(data.startDate).toLocaleDateString()}</td>
                      <td>{new Date(data.endDate).toLocaleDateString()}</td>
                      <td>{data.appliedDuration}</td>
                      <td>{new Date(data.appliedDate).toLocaleDateString()}</td>
                      <td>{data.hrAction}</td>
                      <td>{data.adminAction}</td>
                      <td>
                        <Button
                          variant="primary"
                          size='sm'
                          onClick={() => handleShowModal(data)}
                        >
                          <IoEye />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className='leave-report-table-footer'>
                  <tr>
                    <td>Total</td>
                    <td><strong> {reportData.length}</strong></td>
                    <td colSpan="8">
                    
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Modal for showing leave details */}
        <Modal
          show={showModal}
          onHide={handleCloseModal}
          size="lg"
          centered
        >
           <Modal.Header closeButton className="custom-modal-header">
                        <Modal.Title>Leave Details</Modal.Title>
                      </Modal.Header>
          <Modal.Body>
            {selectedLeave && (
              <div>
                <p><strong>Name:</strong> {selectedLeave.name}</p>
                <p><strong>Leave Type:</strong> {selectedLeave.leaveType}</p>
                <p><strong>Start Date:</strong> {new Date(selectedLeave.startDate).toLocaleDateString()}</p>
                <p><strong>End Date:</strong> {new Date(selectedLeave.endDate).toLocaleDateString()}</p>
                <p><strong>Applied Duration:</strong> {selectedLeave.appliedDuration}</p>
                <p><strong>Applied Date:</strong> {new Date(selectedLeave.appliedDate).toLocaleDateString()}</p>
                <p><strong>HR Approval:</strong> {selectedLeave.hrAction}</p>
                <p><strong>Admin Approval:</strong> {selectedLeave.adminAction}</p>
              </div>
            )}
          </Modal.Body>
        </Modal>
      </LeaveReportLayout>
    </LeaveLayout>
  );
}
